import React from 'react';
import { Progress } from '@/components/ui/progress';
import { Card } from '@/components/ui/card';
import { HardDrive, AlertCircle, CheckCircle, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface StorageProgressBarProps {
  used: number;
  total: number;
  className?: string;
  showUpgradeButton?: boolean;
  compact?: boolean;
}

const StorageProgressBar = ({ 
  used, 
  total, 
  className = '',
  showUpgradeButton = true,
  compact = false
}: StorageProgressBarProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const isUnlimited = total === -1 || total >= 999999;
  const percentage = isUnlimited ? 0 : Math.min(100, Math.round((used / total) * 100));
  const remaining = isUnlimited ? Infinity : Math.max(0, total - used);
  
  // Determine status and colors
  let status: 'healthy' | 'warning' | 'danger' | 'unlimited';
  let statusColor: string;
  let progressColor: string;
  let Icon: React.ElementType;
  
  if (isUnlimited) {
    status = 'unlimited';
    statusColor = 'text-green-600';
    progressColor = 'bg-green-500';
    Icon = CheckCircle;
  } else if (percentage >= 90) {
    status = 'danger';
    statusColor = 'text-red-600';
    progressColor = 'bg-red-500';
    Icon = AlertCircle;
  } else if (percentage >= 70) {
    status = 'warning';
    statusColor = 'text-yellow-600';
    progressColor = 'bg-yellow-500';
    Icon = TrendingUp;
  } else {
    status = 'healthy';
    statusColor = 'text-blue-600';
    progressColor = 'bg-blue-500';
    Icon = HardDrive;
  }

  const handleUpgrade = () => {
    navigate('/subscription');
  };

  if (compact) {
    return (
      <div className={cn("flex items-center gap-3", className)}>
        <Icon className={cn("h-4 w-4", statusColor)} />
        <div className="flex-1 space-y-1">
          <div className="flex justify-between text-xs">
            <span className="font-medium">
              {isUnlimited ? t('storage.unlimited', 'Unlimited') : `${used} MB / ${total} MB`}
            </span>
            {!isUnlimited && (
              <span className={statusColor}>{percentage}%</span>
            )}
          </div>
          {!isUnlimited && (
            <Progress 
              value={percentage} 
              className={cn("h-1", progressColor)}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <Card className={cn("p-4", className)}>
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className={cn("h-5 w-5", statusColor)} />
            <span className="font-semibold text-sm">
              {t('storage.title', 'Storage')}
            </span>
          </div>
          {isUnlimited && (
            <span className={cn("text-xs font-medium px-2 py-1 rounded-full bg-green-100 dark:bg-green-900", statusColor)}>
              {t('storage.unlimited', 'Unlimited')}
            </span>
          )}
        </div>

        {/* Progress Bar */}
        {!isUnlimited && (
          <div className="space-y-2">
            <Progress 
              value={percentage} 
              className={cn("h-2", progressColor)}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{used} MB {t('storage.used', 'used')}</span>
              <span>{remaining} MB {t('storage.remaining', 'remaining')}</span>
            </div>
          </div>
        )}

        {/* Status Message */}
        {!isUnlimited && (
          <div className="space-y-2">
            {status === 'danger' && (
              <p className="text-xs text-red-600">
                {t('storage.dangerMessage', 'Your storage is almost full! Upgrade to continue uploading.')}
              </p>
            )}
            {status === 'warning' && (
              <p className="text-xs text-yellow-600">
                {t('storage.warningMessage', 'You\'re running out of storage space.')}
              </p>
            )}
            
            {/* Upgrade Button */}
            {showUpgradeButton && (status === 'danger' || status === 'warning') && (
              <Button 
                onClick={handleUpgrade}
                size="sm"
                variant={status === 'danger' ? 'default' : 'outline'}
                className="w-full"
              >
                {t('storage.upgradeButton', 'Upgrade for More Storage')}
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default StorageProgressBar;
